import React, { ReactElement, useCallback, useState, useMemo, useEffect } from "react";
import {
    Drawer,
    DrawerBody,
    DrawerFooter,
    DrawerHeader,
    DrawerHeaderTitle,
} from "src/components";
import { OperationalTargetFormDrawerProps, OperationalTargetFormDrawerState } from "./OperationalTargetFormDrawer.types";
import { OperationalTargetModel, createOperationalTargetAsync, updateOperationalTargetAsync } from "src/api";
import { OperationalTargetForm } from "../OperationalTargetForm";
import { localize } from "src/l10n";
import { useDispatch, useSelector } from "react-redux";
import { setConfirmPopup } from "src/popups/actions";
import { operationalTargetCreated, operationalTargetUpdated } from "src/redux";
import { UnstyledButton } from "src/ui";
import { SmallBodyBolder } from "src/components/Text";
import { PrimaryButton } from "@fluentui/react";

function getInitialModel(): OperationalTargetModel {
    const now = new Date();

    return {
        nodeId: 0,
        target: "",
        focus: "",
        startDate: new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate(),
            0, 0, 0, 0),
    };
}

/**
 * OperationalTargetFormDrawer component
 *
 * @param {OperationalTargetFormDrawerProps} props - The component props
 * @returns {ReactElement | null} The rendered component
 */
function OperationalTargetFormDrawer({
    onDismiss,
    open,
    operationalTarget,
}: OperationalTargetFormDrawerProps): ReactElement | null {
    const dispatch = useDispatch();

    const departmentId = useSelector<Spintr.AppState, number>(
        (appState) => appState.profile.active.department?.id,
    );

    const [state, setState] = useState<OperationalTargetFormDrawerState>({
        isSubmitting: false,
        isDirty: false,
    });
    const { isSubmitting } = state;
    const [model, setModel] = useState<OperationalTargetModel>(getInitialModel());

    useEffect(() => {
        let newModel = getInitialModel();

        if (operationalTarget) {
            newModel = { ...newModel, ...operationalTarget };
        }

        setModel(newModel);
        setState((prevState) => ({ ...prevState, isDirty: false }));
    }, [operationalTarget]);

    const isNew = useMemo(() => !operationalTarget?.id, [operationalTarget]);

    const l10n = useMemo(() => ({
        createNew: localize("CREATE_OPS_TARGET"),
        edit: localize("Redigera"),
        cancel: localize("Avbryt"),
        save: localize(isNew ? "CREATE_OPS_TARGET" : "Spara"),
    }), [isNew]);

    const onModelUpdating = useCallback(
        (updater: (model: OperationalTargetModel) => OperationalTargetModel) => {
            setModel((prev) => updater(prev));
            setState((prevState) => ({ ...prevState, isDirty: true }));
        }, [setModel]);

    const onSubmitClick = useCallback(async () => {
        if (isSubmitting) {
            return;
        }

        setState((prevState) => ({ ...prevState, isSubmitting: true }));

        let opsTarget: Spintr.OperationalTarget | undefined;
        try
        {
            const modelToSend = { ...model, nodeId: departmentId || 0 };
            opsTarget = await (isNew
                ? createOperationalTargetAsync(modelToSend)
                : updateOperationalTargetAsync(operationalTarget.id, modelToSend));
        } catch (err) {
            console.error(err);
        }

        setState((prevState) => ({ ...prevState, isSubmitting: false }));

        if (!opsTarget) {
            return;
        }

        dispatch(isNew
            ? operationalTargetCreated(opsTarget)
            : operationalTargetUpdated(opsTarget));

        setModel(getInitialModel());
        setState((state) => ({ ...state, isDirty: false }));
        onDismiss();
    }, [model, isSubmitting, setState, setModel, isNew, departmentId]);

    const onCancelClick = useCallback(
        () => {
            if (!state.isDirty) {
                setModel(getInitialModel());
                onDismiss();
                return;

            }

            dispatch(setConfirmPopup({
                isOpen: true,
                message: localize("UnsavedChangesWarning"),
                onConfirm: () => {
                    setModel(getInitialModel());
                    setState((state) => ({ ...state, isDirty: false }));
                    onDismiss();
                },
            }));
        }, [onDismiss, state.isDirty, setModel, setState, dispatch]);

    const onOpenChange = useCallback((open: boolean) => {
        if (open) {
            return;
        }

        onCancelClick();
    }, [onCancelClick]);

    return (
        <Drawer className="OperationalTargetFormDrawer"
                onOpenChange={onOpenChange}
                open={open}
                position="end"
                size="medium">
            <DrawerHeader>
                <DrawerHeaderTitle>
                    {isNew ? l10n.createNew : l10n.edit}
                </DrawerHeaderTitle>
            </DrawerHeader>
            <DrawerBody>
                <OperationalTargetForm model={model}
                                       onModelUpdating={onModelUpdating} />
            </DrawerBody>
            <DrawerFooter className="OperationalTargetFormDrawer-footer">
                <UnstyledButton disabled={isSubmitting}
                                onClick={onCancelClick}>
                    <SmallBodyBolder color="contentLight">
                        {l10n.cancel}
                    </SmallBodyBolder>
                </UnstyledButton>
                <PrimaryButton className="QualityHubTaskFormDrawer-save"
                               disabled={isSubmitting}
                               onClick={onSubmitClick}
                               type="submit">
                    {l10n.save}
                </PrimaryButton>
            </DrawerFooter>
        </Drawer>
    );
}

export default OperationalTargetFormDrawer;

